import React from 'react';
import { Link } from 'react-router-dom';
import { Callout, H2, H3, OL, P, Strong, Table, UL } from './prose';
import { ProductVideo } from './ProductVideo';

export interface BlogFaq {
  q: string;
  a: string;
}

export interface BlogPost {
  slug: string;
  /** On-page H1. */
  title: string;
  /** <title> / og:title; keep to roughly 60 characters so SERPs don't truncate it. */
  seoTitle: string;
  faq?: BlogFaq[];
  /** Path under /public of a 2400x1260 social image; omit to use the site-wide og-image.png. */
  ogImage?: string;
  /** Used for <meta name="description">, OG/Twitter and the index card. */
  description: string;
  /** ISO date (YYYY-MM-DD). */
  date: string;
  readingMinutes: number;
  Body: React.FC;
}

/** Rendered on the page and emitted as FAQPage JSON-LD, so the two never drift apart. */
const FESTIVE_FAQ: BlogFaq[] = [
  {
    q: 'How do I set a Diwali budget in Hisab Kitab?',
    a: 'Open Budgets and create a one-time budget with a start and end date that covers the festive period, then add a budget for each category you want to watch, such as Gifts, Food and Travel. The budget shows how much you have spent and the percentage used.',
  },
  {
    q: 'How do I split a festive dinner bill with family?',
    a: 'Create a group, add your family members by name and email, then add the bill as an expense, choose who paid and who shared it, and split it equally, by exact amounts or by percentage.',
  },
  {
    q: 'Can people pay back only part of what they owe?',
    a: 'Yes. A settlement can be full or partial, and the Simplify option shows the smallest set of payments that clears the whole group.',
  },
  {
    q: 'Is Hisab Kitab available on iPhone?',
    a: 'Hisab Kitab is currently available on Android through Google Play.',
  },
];

const FestiveBudgetsAndSplits: React.FC = () => (
  <>
    <P>
      The festive season in India is full of moments worth spending on—new clothes, sweets, gifts,
      travel, decorations, and dinners with the people you love. But it is also easy for small
      purchases to add up quietly. By the time the celebrations end, your bank balance often tells a
      different story.
    </P>
    <P>
      That is where <Strong>Budgets</Strong> and <Strong>Splits</Strong> in{' '}
      <Link to="/" className="text-yellow-400 hover:underline">
        Hisab Kitab
      </Link>{' '}
      can help.
      Budgets give your festive spending a clear limit, while Splits keep shared expenses fair and
      transparent. Together, they help you enjoy the season without turning the next month into a
      financial recovery period.
    </P>

    <H2>Why festive spending gets messy</H2>
    <P>Festive expenses rarely arrive as one large purchase. They come in pieces:</P>
    <UL>
      <li>A gift for a family member</li>
      <li>Sweets for neighbours</li>
      <li>Travel tickets home</li>
      <li>Decorations for the house</li>
      <li>A group dinner after a celebration</li>
      <li>A contribution to a family pooja or event</li>
    </UL>
    <P>
      Each expense feels reasonable on its own. The problem is that they are spread across different
      days, payment methods, and sometimes different people.
    </P>
    <P>
      Without a plan, you usually discover the total only after the spending is done. With a budget,
      you can see the total <em>while</em> you still have time to adjust.
    </P>

    <H2>Start with a Diwali and festive season budget</H2>
    <P>
      Before the shopping begins, create a simple festive budget. You do not need to plan every
      rupee—start with the categories that usually take the biggest share of your spending. Here is
      an example (use numbers that fit your own household):
    </P>
    <Table
      head={['Category', 'Example budget']}
      rows={[
        ['Gifts', '₹8,000'],
        ['Sweets and food', '₹3,000'],
        ['Travel', '₹5,000'],
        ['Decorations', '₹2,000'],
        ['Outings and celebrations', '₹2,000'],
      ]}
    />
    <P>
      In Hisab Kitab, open <Strong>Budgets</Strong> and create one for each category you want to
      watch. Gifts and Travel are built-in categories; Decorations and Sweets are not, so add them
      under Categories first if you want a budget of their own.
    </P>

    <H3>Make it a one-time festive budget</H3>
    <P>
      Hisab Kitab budgets can recur monthly or weekly, but they can also be <Strong>one-time</Strong>:
      pick a start and end date and the budget covers only that window. That is a natural fit for
      the festive fortnight, since it does not roll into next month and it stops counting once the
      celebrations are over. You can also add an optional date range to a recurring budget, such as a
      monthly food budget that runs only until the end of the festive month.
    </P>

    <H3>See progress before you overspend</H3>
    <P>
      Each budget shows how much you have spent, how much remains, and the percentage used. You can
      set an alert threshold (80% by default), and the progress indicator changes colour as you
      approach the limit, so you notice before you have crossed it.
    </P>

    <H3>A simple rule</H3>
    <P>
      Set a budget for your <Strong>top three or four spending categories</Strong>, not every
      possible category. For most people, that means gifts, food, travel, and celebrations. If you
      would rather track a single limit, make one overall budget or one budget that covers several
      categories together. Note that a category can sit in only one active recurring budget at a
      time, so pick the grouping you prefer up front. This keeps the system useful without making it
      feel like homework.
    </P>

    <H2>Record expenses as they happen</H2>
    <P>
      The biggest budgeting mistake is waiting until the end of the week to add expenses. By then,
      the small UPI payments, cash purchases, and card transactions blur together.
    </P>
    <P>Instead, add each festive expense as soon as possible:</P>
    <OL>
      <li>Open Hisab Kitab.</li>
      <li>Add the expense amount.</li>
      <li>Choose the relevant category, such as Gifts, Food, or Travel.</li>
      <li>Add a short note if needed, such as “Diwali gift for Mummy” or “Sweets for office.”</li>
      <li>Check the remaining budget before making the next purchase.</li>
    </OL>
    <ProductVideo
      src="/hisab-kitab-add-expense.mp4"
      poster="/hisab-kitab-add-expense-poster.jpg"
      width={860}
      height={1920}
      label="Screen recording of the Hisab Kitab Android app: entering 1200 for a gift, choosing the Gifts category and an account, saving the expense, and seeing it in the transaction list."
      caption="Adding an expense in Hisab Kitab"
    />
    <P>
      Hisab Kitab also offers an optional SMS reader. If you grant the permission, it reads
      transaction SMS from your bank and payment apps and drafts entries for you to approve on the
      Review tab. That can cut down manual entry, but it is still worth reviewing each draft and
      checking its category so your festive budget stays accurate. Details on what the app reads and
      stores are in our{' '}
      <Link to="/privacy-policy" className="text-yellow-400 hover:underline">
        privacy policy
      </Link>
      .
    </P>

    <H2>Use Splits for shared celebrations</H2>
    <P>
      Festivals are rarely a solo financial event. You may split a family dinner, contribute to a
      group gift, share travel costs, or settle expenses with friends after an outing.
    </P>
    <P>
      Instead of maintaining mental notes, screenshots, or a confusing chat thread, create a group in
      Hisab Kitab for the occasion. For example:
    </P>
    <UL>
      <li>
        <Strong>Family Diwali Dinner</Strong>
      </li>
      <li>
        <Strong>Office Secret Santa</Strong>
      </li>
      <li>
        <Strong>Goa Trip</Strong>
      </li>
      <li>
        <Strong>Flatmates’ Festive Groceries</Strong>
      </li>
      <li>
        <Strong>Cousins’ Celebration Fund</Strong>
      </li>
    </UL>
    <P>
      When you create a group, you add members by name and email, so everyone is part of the same
      record from the first expense.
    </P>

    <H3>Example: family dinner</H3>
    <P>
      Suppose your family goes out for a festive dinner costing ₹6,000, and you pay the bill.
    </P>
    <OL>
      <li>Open your family group and add an expense of ₹6,000.</li>
      <li>Select yourself as the payer.</li>
      <li>Choose the members who shared the meal.</li>
      <li>
        Pick how to split it: <Strong>equally</Strong>, by <Strong>exact amounts</Strong> (when some
        people ordered separately), or by <Strong>percentage</Strong>.
      </li>
    </OL>
    <ProductVideo
      src="/hisab-kitab-split-expense.mp4"
      poster="/hisab-kitab-split-expense-poster.jpg"
      width={720}
      height={1610}
      label="Screen recording of the Hisab Kitab Android app: adding a 6,000 rupee dinner expense to a group, choosing the payer, splitting it equally among five members, and seeing each member's balance."
      caption="Splitting an expense in a group"
    />
    <P>
      Now everyone can see what was spent, who paid, and what they owe. No one has to remember a
      WhatsApp message from three days ago.
    </P>
    <Callout>
      <Strong>Splits and your budget:</Strong> when you split an expense, your own share is what
      counts toward your budget, not the full bill you paid up front.
    </Callout>

    <H2>Settle balances without awkwardness</H2>
    <P>
      The hardest part of group expenses is not adding them—it is getting everyone settled without
      repeated reminders.
    </P>
    <P>
      Hisab Kitab keeps shared records in one place, so the group can see who owes whom. If a group
      has several people owing each other, tap <Strong>Simplify</Strong> to see the smallest set of
      payments that clears everything, which means fewer separate transactions overall.
    </P>
    <P>
      For example, if you paid ₹6,000 for dinner and three family members owe you ₹1,500 each, they
      can repay you over the next few days. Settlements can be <Strong>full or partial</Strong>: when
      someone pays back, record the amount in the app so the group balance stays accurate.
    </P>

    <H2>A practical festive workflow</H2>
    <P>Here is a simple workflow you can follow from the first day of the season:</P>
    <OL>
      <li>
        <Strong>Create your festive categories and budgets.</Strong> Start with gifts, food, travel,
        decorations, and outings.
      </li>
      <li>
        <Strong>Add every expense immediately.</Strong> Even a ₹200 sweet purchase matters when it
        happens five times.
      </li>
      <li>
        <Strong>Create a group for shared expenses.</Strong> Use one group per trip, family event,
        or celebration.
      </li>
      <li>
        <Strong>Split expenses fairly.</Strong> Record who paid and who benefited.
      </li>
      <li>
        <Strong>Settle as payments come in.</Strong> Do not leave balances unclear until after the
        festival.
      </li>
      <li>
        <Strong>Review your spending.</Strong> Use category insights and trends to understand where
        your money went.
      </li>
    </OL>

    <H2>What to review after the festival</H2>
    <P>Once the celebrations are over, spend ten minutes reviewing your records. Ask yourself:</P>
    <UL>
      <li>Which category crossed its budget?</li>
      <li>Were there expenses you forgot to plan for?</li>
      <li>Did group expenses create confusion or delays?</li>
      <li>Which categories deserve a higher or lower budget next time?</li>
    </UL>
    <P>
      This review turns one festive season’s spending into useful information for the next one.
      Instead of starting from zero every year, you can build a realistic festive budget based on
      your actual habits.
    </P>

    <H2>Enjoy the season, not the stress</H2>
    <P>
      Festivals are meant to be enjoyed. Budgets are not about spending less on things that
      matter—they are about making room for them intentionally.
    </P>
    <P>
      Use <Strong>Budgets</Strong> to decide how much you want to spend before the season begins. Use{' '}
      <Strong>Splits</Strong> to keep shared expenses clear, fair, and easy to settle. Then use your
      spending insights to make the next celebration even smoother.
    </P>
    <P>Your money, your people, and your celebrations can all stay in one clear picture.</P>

    <H2>Frequently asked questions</H2>
    {FESTIVE_FAQ.map(({ q, a }) => (
      <div key={q}>
        <H3>{q}</H3>
        <P>{a}</P>
      </div>
    ))}
  </>
);

/** Newest first. */
export const POSTS: BlogPost[] = [
  {
    slug: 'festive-budgets-and-splits',
    title: 'Celebrate More, Stress Less: Use Budgets and Splits to Stay in Control This Festive Season',
    seoTitle: 'Festive Budget & Splits Guide for Diwali | Hisab Kitab',
    faq: FESTIVE_FAQ,
    ogImage: '/og-blog-festive.png',
    description:
      'Learn how to use Budgets and Splits in Hisab Kitab to manage festive spending, track shared expenses, and settle group costs without stress.',
    date: '2026-10-19',
    readingMinutes: 6,
    Body: FestiveBudgetsAndSplits,
  },
];

export const getPost = (slug: string) => POSTS.find((p) => p.slug === slug);

export const SITE = 'https://hisab-kitab.maniac-tech.com';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.maniactech.hisabkitabfinance';

/** Play Store link tagged so blog installs are attributable. */
export const blogPlayStoreUrl = (campaign: string) =>
  `${PLAY_STORE_URL}&referrer=${encodeURIComponent(`utm_source=blog&utm_medium=article&utm_campaign=${campaign}`)}`;

export const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
